import * as _ from 'underscore';
import { Messages } from '../../codegen/Localize';
import { IListFetcher } from '../fetchers/IListFetcher';
import { Filter } from '../helpers/Filter';
import { RequestHelper } from '../helpers/RequestHelper';
import { ServerApiMapper } from '../helpers/ServerModelHelper';
import { SliceableHelper } from '../helpers/SliceableHelper';
import { ConnectionDetailInfo, isRemote } from '../models/ConnectionDetailInfo';
import { IDataConnectionInfo } from '../models/IDataConnectionInfo';
import { IDataSourceBase } from '../models/IDataSourceInfo';
import { Set } from '../models/Set';
import { Module } from '../Module';
import {
  CheckConnectionRequest,
  ICheckConnectionParams,
  ICheckConnectionResult,
} from '../ServerApi/requests/CheckConnectionRequest';
import { GeneratePublicKeyRequest, IGeneratePublicKeyResult } from '../ServerApi/requests/GeneratePublicKeyRequest';
import { GetDataConnectionIdsFromDatasourcesRequest } from '../ServerApi/requests/GetDataConnectionIdsFromDatasourcesRequest';
import { GetDataConnectionsRequest } from '../ServerApi/requests/GetDataConnectionsRequest';
import { GetDatasourceActionsRequest } from '../ServerApi/requests/GetDatasourceActionsRequest';
import { GetFieldValuesRequest } from '../ServerApi/requests/GetFieldValuesRequest';
import { UpdateConnectionsOAuthRequest } from '../ServerApi/requests/UpdateConnectionsOAuthRequest';
import { IUpdateConnectionsParams, UpdateConnectionsRequest } from '../ServerApi/requests/UpdateConnectionsRequest';
import { DatasourceIdToConnectionIdsMap } from '../ServerApi/types/DatasourceIdToConnectionIdsMap';
import { IDatasourceActions } from '../ServerApi/types/IDatasourceActions';
import { IFieldValue } from '../ServerApi/types/IFieldValue';
import { FilterFieldNames } from '../ServerApi/types/IFilterClause';
import { IResult } from '../ServerApi/types/IResponse';
import { ErrorNotifyingServer } from '../services/ErrorNotifyingServer';
import { FetcherFactory } from '../services/FetcherFactory';
import { LongRunningActionService } from '../services/LongRunningActionService';
import { ServerService } from '../services/ServerService';
import { FieldValuesQuery } from './FieldValuesQuery';
import { IActionResource, IFieldValuesResource, IListResource } from './Resource';
import { ResourceQuery } from './ResourceQuery';

export interface IEmbeddedPasswordUpdate {
  isEmbedded: boolean;
  newPassword?: string;
}

// Represents the intended updates to a data connection. All fields are optional.
export interface IConnectionUpdate {
  server?: string;
  port?: string;
  username?: string;
  passwordUpdate?: IEmbeddedPasswordUpdate;
  usingRemoteQueryAgent?: boolean;
}

export class DataConnections implements IListResource<IDataConnectionInfo>, IFieldValuesResource, IActionResource<IDatasourceActions> {

  public static getDisplayUsername(dataconnection: IDataConnectionInfo): string {
    const details: ConnectionDetailInfo = dataconnection.connectionDetails;
    let text = '';
    if (details && isRemote(details)) {
      text = details.databaseUsername;
    }
    return text;
  }

  public static getDisplayAuthentication(dataconnection: IDataConnectionInfo): string {
    const details: ConnectionDetailInfo = dataconnection.connectionDetails;
    let text = '';
    if (details) {
      if (isRemote(details)) {
        if (details.hasEmbeddedPassword) {
          text = Messages.hasPasswordEmbedded();
        } else {
          text = Messages.hasPasswordNotEmbedded();
        }
      } else if (details.type === 'file') {
        text = Messages.none();
      }
    }
    return text;
  }

  public static $inject = ['$q', 'ErrorNotifyingServer', 'LongRunningActionService', 'FetcherFactory'];

  private server: ServerService;
  private listFetcher: IListFetcher<IDataConnectionInfo>;
  private allQuery: ResourceQuery<IDataConnectionInfo>;
  private fieldValuesFetcher: IListFetcher<IFieldValue>;

  constructor(
      private $q: ng.IQService,
      private ErrorNotifyingServer: ErrorNotifyingServer,
      private LongRunningActionService: LongRunningActionService,
      FetcherFactory: FetcherFactory
  ) {
    this.server = ErrorNotifyingServer.getServerService();
    this.listFetcher = FetcherFactory.getServerResourceFetcher(GetDataConnectionsRequest, ServerApiMapper.dataConnections);
    this.allQuery = new ResourceQuery(this.listFetcher);
    this.fieldValuesFetcher = FetcherFactory.getServerResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);
  }

  public all(): ResourceQuery<IDataConnectionInfo> {
    return this.allQuery;
  }

  public getConnectionIdsFromDatasourceIds(datasourceIds: string[]): ng.IPromise<DatasourceIdToConnectionIdsMap> {
    return this.ErrorNotifyingServer.sendRequests(
      RequestHelper.buildRequests(datasourceIds, (idsBlock) =>
        new GetDataConnectionIdsFromDatasourcesRequest(idsBlock)
      ),
      (results: DatasourceIdToConnectionIdsMap[]) => {
        return Object.assign({}, ...results);
      });
  }

  public getFromDatasource(datasource: IDataSourceBase): ng.IPromise<IDataConnectionInfo[]> {
    return this.getFromDatasources(Set.singleton(datasource, ds => ds.id));
  }

  public getFromDatasources(datasources: Set<IDataSourceBase>): ng.IPromise<IDataConnectionInfo[]> {
    const datasourceIds = datasources.map(ds => ds.id);
    const makeQueryFromDatasourceIdsBlock = (idsBlock: string[]) =>
        this.allQuery.addFilter(Filter.stringFieldInArray(FilterFieldNames.datasourceId, idsBlock));

    return SliceableHelper.fetchAllForSliceableWithArrayParam(datasourceIds, makeQueryFromDatasourceIdsBlock, this.$q);
  }

  public getForFlow(flowId: string): ng.IPromise<IDataConnectionInfo[]> {
    const query = this.allQuery.addFilter(Filter.stringFieldEq(FilterFieldNames.flowId, flowId));
    return SliceableHelper.fetchAll(query).then(listResult => listResult.items);
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'dataConnection');
  }

  public actions(datasourceId: string): ng.IPromise<IDatasourceActions> {
    // Since actions/ownership are defined at a data source level, we get the available actions
    // for the connection's data source and the UI shows only those that are relevant to connections.
    // TODO: Figure out how to refactor this to get the action on the datasource ID instead of the connection ID.
    // It'll work for now, but will break when the unified datasources go away on the backend.
    return this.server.sendRequest(new GetDatasourceActionsRequest(datasourceId));
  }

  public checkExistingConnection(dataSourceId: string): ng.IPromise<ICheckConnectionResult> {
    const request = new CheckConnectionRequest({ id: dataSourceId });
    return this.ErrorNotifyingServer.sendRequest(request);
  }

  public checkConnection(dataSourceId: string, connectionUpdate: IConnectionUpdate): ng.IPromise<ICheckConnectionResult> {

    const requestParams: ICheckConnectionParams =
    {
      id: dataSourceId,
      server: connectionUpdate.server,
      port: connectionUpdate.port,
      username: connectionUpdate.username
    };

    return this.setPasswordParams(requestParams, connectionUpdate.passwordUpdate).then(() => {
      return this.ErrorNotifyingServer.sendRequest(new CheckConnectionRequest(requestParams));
    });
  }

  public updateConnections(dataSourceIds: string[], connectionUpdate: IConnectionUpdate): ng.IPromise<IResult> {

    const requestParams: IUpdateConnectionsParams =
    {
      ids: dataSourceIds,
      server: connectionUpdate.server,
      port: connectionUpdate.port,
      username: connectionUpdate.username,
      usingRemoteQueryAgent: connectionUpdate.usingRemoteQueryAgent
    };

    return this.setPasswordParams(requestParams, connectionUpdate.passwordUpdate)
      .then(() => this.ErrorNotifyingServer.sendRequest(new UpdateConnectionsRequest(requestParams)))
      .then(this.LongRunningActionService.startProgressTrackingForResult<IResult>())
      .then(this.LongRunningActionService.delayResultForSolr<IResult>());
  }

  public updateConnectionsOAuth(dataSourceIds: string[], keychainEntryId?: string): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer
      .sendRequest(new UpdateConnectionsOAuthRequest({
        ids: dataSourceIds,
        keychainEntryId: keychainEntryId
      }))
      .then(this.LongRunningActionService.startProgressTrackingForResponse<IResult>())
      .then(this.LongRunningActionService.delayResultForSolr<IResult>());
  }

  private setPasswordParams(
      requestParams: { embedPassword?: boolean, encryptedPassword?: string, encryptedPasswordKeyId?: string },
      passwordUpdate: IEmbeddedPasswordUpdate): ng.IPromise<any> {

    if (passwordUpdate) {

      requestParams.embedPassword = passwordUpdate.isEmbedded;

      if (_.isString(passwordUpdate.newPassword)) {
        return this.server.sendRequest(new GeneratePublicKeyRequest()).
          then((generatePublicKeyResult: IGeneratePublicKeyResult) => {
            requestParams.encryptedPassword = rsa.encrypt(passwordUpdate.newPassword, generatePublicKeyResult.key);
            requestParams.encryptedPasswordKeyId = generatePublicKeyResult.keyId;
          });
      }
    }

    return this.$q.when({});
  }

}

Module.instance().service('DataConnections', DataConnections);
